import { useState } from "react"
import type {
  CreatePartnershipRowFromAdRequest,
  LinkPartnershipAdRequest,
  PartnershipAdDto,
  PartnershipContentRowDto,
} from "~/ui-rtk/api/types/partnerships.types"
// Direct import (not the `common` barrel) — see CreativesTab.tsx.
import Card from "~/ui-rtk/components/ui/common/Card"
import { humanize } from "./partnership-copy"
import { CreateRowFromAdDialog, LinkAdDialog } from "./PartnershipDialogs"
import { StatusChip, toDay, type ChipTone } from "./PartnershipsTable"

/**
 * Partner ads Meta returned (branded-content / creator-page creative) that no
 * tracker row claims yet. Two ways out: link to an existing post, or create a
 * creator + manual row from the ad itself.
 */

export interface UnmatchedAdsSectionProps {
  ads: PartnershipAdDto[]
  contentRows: PartnershipContentRowDto[]
  onLink: (adId: string, body: LinkPartnershipAdRequest) => Promise<boolean>
  onCreateRow: (
    adId: string,
    body: CreatePartnershipRowFromAdRequest,
  ) => Promise<boolean>
}

const HEADER_CLS =
  "px-2 py-1.5 border-s border-s-basic-blue bg-basic-dark-blue text-md text-white font-bold text-left"
const CELL_CLS = "px-2 py-1.5 border-s border-s-basic-blue text-sm align-top"

function effectiveStatusTone(status: string | null): ChipTone {
  switch (status) {
    case "ACTIVE":
      return "green"
    case "PAUSED":
    case "ADSET_PAUSED":
    case "CAMPAIGN_PAUSED":
      return "amber"
    case "DISAPPROVED":
    case "WITH_ISSUES":
      return "red"
    default:
      return "grey"
  }
}

export default function UnmatchedAdsSection({
  ads,
  contentRows,
  onLink,
  onCreateRow,
}: UnmatchedAdsSectionProps) {
  const [linkTarget, setLinkTarget] = useState<PartnershipAdDto | null>(null)
  const [createTarget, setCreateTarget] = useState<PartnershipAdDto | null>(
    null,
  )

  return (
    <Card
      rounded
      className="border border-basic-blue bg-basic-dark-blue space-y-3"
    >
      <div className="flex items-baseline justify-between gap-2 px-2">
        <h2 className="text-base font-semibold text-white">
          Live partnership ads with no tracker row
        </h2>
        <span className="text-xs text-gray-400">{ads.length} unmatched</span>
      </div>
      {ads.length === 0 ? (
        <p className="px-2 pb-1 text-sm text-gray-400">
          Every partner ad Meta returned is linked to a tracked post. New ones
          show up here after the next refresh.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="w-full border-b border-b-basic-blue">
              <tr>
                <th className={HEADER_CLS}>Ad</th>
                <th className={HEADER_CLS}>Creator page</th>
                <th className={HEADER_CLS}>Status</th>
                <th className={HEADER_CLS}>First spend</th>
                <th className={HEADER_CLS}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ads.map(ad => (
                <tr
                  key={ad.id}
                  data-testid={`unmatched-ad-${ad.id}`}
                  className="border-b border-b-basic-blue bg-basic-black hover:bg-basic-dark-blue/70"
                >
                  <td className={`${CELL_CLS} max-w-96 overflow-hidden`}>
                    <div className="flex min-w-0 flex-col">
                      <span
                        className="truncate font-medium text-white"
                        title={ad.adName}
                      >
                        {ad.adName}
                      </span>
                      <span className="truncate text-xs text-gray-400">
                        ID: {ad.adExternalId}
                        {ad.isArmedUnderPausedParent &&
                          " · armed under paused parent"}
                      </span>
                    </div>
                  </td>
                  <td
                    className={`${CELL_CLS} whitespace-nowrap text-xs text-gray-400`}
                  >
                    {ad.creatorPageId ?? "—"}
                  </td>
                  <td className={CELL_CLS}>
                    <div className="flex flex-col items-start gap-0.5">
                      <StatusChip
                        tone={effectiveStatusTone(ad.effectiveStatus)}
                      >
                        {ad.effectiveStatus
                          ? humanize(ad.effectiveStatus)
                          : "Unknown"}
                      </StatusChip>
                      {ad.status && ad.status !== ad.effectiveStatus && (
                        <span className="text-xs text-gray-400">
                          set to {humanize(ad.status)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td
                    className={`${CELL_CLS} whitespace-nowrap text-xs text-gray-400`}
                  >
                    {toDay(ad.firstSpendAt) ?? "—"}
                  </td>
                  <td className={CELL_CLS}>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <button
                        type="button"
                        onClick={() => setLinkTarget(ad)}
                        className="text-xs font-medium text-brand hover:underline"
                      >
                        Link…
                      </button>
                      <button
                        type="button"
                        onClick={() => setCreateTarget(ad)}
                        className="text-xs font-medium text-system-blue-300 hover:underline"
                      >
                        Create row
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LinkAdDialog
        ad={linkTarget}
        contentRows={contentRows}
        onClose={() => setLinkTarget(null)}
        onSubmit={onLink}
      />
      <CreateRowFromAdDialog
        ad={createTarget}
        onClose={() => setCreateTarget(null)}
        onSubmit={onCreateRow}
      />
    </Card>
  )
}
